import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import TopBar from '../components/TopBar.jsx';
import { apiGet } from '../lib/api.js';
import { Bot, Plus, MessageSquare, Phone, Sparkles, Pause, Play } from 'lucide-react';

export default function AIBots() {
  const nav = useNavigate();
  const { data: bots = [] } = useQuery({ queryKey: ['aibots'], queryFn: () => apiGet('/aibots') });

  const list = bots;

  return (
    <div className="space-y-4">
      <TopBar title="AI Bots" subtitle="Programmable outbound SMS + voice agents" />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Sparkles className="w-4 h-4 text-civix-purple" />
          {list.length} bots — programmed to text, call, qualify, and book on your behalf
        </div>
        <button onClick={() => nav('/aibots/new')} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> New AI Bot
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((b) => (
          <div key={b._id} className="panel p-5 hover:border-civix-purple cursor-pointer transition" onClick={() => nav(`/aibots/${b._id}`)}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-lg civix-gradient flex items-center justify-center shadow-glow-purple">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                {b.status === 'active' ? <Play className="w-3 h-3 inline mr-1" /> : <Pause className="w-3 h-3 inline mr-1" />}
                {b.status}
              </span>
            </div>
            <div className="font-semibold mb-1">{b.name}</div>
            <div className="flex items-center gap-2 text-xs text-white/50 mb-4">
              {b.type === 'sms' && <><MessageSquare className="w-3 h-3" /> SMS</>}
              {b.type === 'voice' && <><Phone className="w-3 h-3" /> Voice</>}
              {b.type === 'hybrid' && <><MessageSquare className="w-3 h-3" /> + <Phone className="w-3 h-3" /> Hybrid</>}
              <span className="ml-2">· {b.persona?.tone || 'friendly'} tone</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-civix-border">
              <div>
                <div className="text-lg font-semibold">{b.stats?.messagesSent?.toLocaleString() || 0}</div>
                <div className="text-[10px] text-white/40 uppercase">Texts</div>
              </div>
              <div>
                <div className="text-lg font-semibold">{b.stats?.callsMade?.toLocaleString() || 0}</div>
                <div className="text-[10px] text-white/40 uppercase">Calls</div>
              </div>
              <div>
                <div className="text-lg font-semibold gradient-text">{b.stats?.bookings?.toLocaleString() || 0}</div>
                <div className="text-[10px] text-white/40 uppercase">Booked</div>
              </div>
            </div>
          </div>
        ))}

        <div onClick={() => nav('/aibots/new')} className="panel p-5 border-dashed flex flex-col items-center justify-center text-center cursor-pointer hover:border-civix-purple min-h-[240px]">
          <div className="w-12 h-12 rounded-full bg-civix-purple/20 flex items-center justify-center mb-3">
            <Plus className="w-6 h-6 text-civix-purple" />
          </div>
          <div className="font-semibold">Build a new bot</div>
          <div className="text-xs text-white/50 mt-1">Persona · Goals · Knowledge · Guardrails</div>
        </div>
      </div>
    </div>
  );
}
